import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactSlider from 'react-slider';
import classNames from 'classnames';
import _ from 'lodash';
import Header from '../Header';
import ParentalIcons from '../ParentalIcons';
import ForgotPasswordCTA from '../ForgotPasswordCTA';
import { fetchPCS } from './ParentalControlsActions';
import { makeSelectAppPCS } from './selectors';

import Style from './ParentalControls.module.scss';

const ParentalControls = ({
  account = { accessToken: '' },
  forgotPasswordCTAProps = {
    containerClass: '',
    linkClass: '',
    linkText: '',
    onClick: () => {},
    text: '',
    theme: '',
  },
  getRatingImageURL = r => r,
  handleMovieRatingChange = () => {},
  handleTvRatingChange = () => {},
  headerProps = {},
  language = {
    profile_pcsContinueLabel: '',
    profile_pcsCurrentMovieLabel: '',
    profile_pcsCurrentTvLabel: '',
    profile_pcsInstructions: '',
    profile_pcsPasswordPrompt: '',
    profile_pcsSaveLabel: '',
    profile_passwordInvalidError: '',
  },
  onFieldChange = e => e,
  onSaveParentalControls = pcs => pcs,
  onVerifyPassword = () => {},
  parentalControlsNewMovieRatingIndex: newMovieIdx = 0,
  parentalControlsNewTvRatingIndex: newTvIdx = 0,
  parentalControlsPassword = null,
  parentalControlsPasswordError = null,
  parentalControlsPasswordVerified = false,
  parentalControlsShowPassword = false,
  pcs,
  dispatch,
}) => {
  const {
    profile_pcsContinueLabel: continueLabel,
    profile_pcsCurrentMovieLabel: currentMovieLabel,
    profile_pcsCurrentTvLabel: currentTvLabel,
    profile_pcsInstructions: instructions,
    profile_pcsPasswordPrompt: passwordPrompt,
    profile_pcsSaveLabel: saveLabelText,
    profile_passwordInvalidError: passwordInvalidError,
  } = language || {};

  const {
    pcsRatings: { tv, mpaa: allMovieRatings },
  } = window.ExternalConfig;

  const { accessToken } = account;

  useEffect(() => {
    dispatch(fetchPCS(accessToken));
  }, [accessToken, dispatch]);

  const allTvRatings = _.filter(tv, 'parentalControlEnabled');
  const { tv_rating_val: tvVal } = pcs;
  const { mv_rating_val: mvVal } = pcs;

  const defaultTvRatingIndex = tvVal
    ? _.findIndex(allTvRatings, { label: tvVal })
    : allTvRatings.length - 1;
  const defaultMovieRatingIndex = mvVal
    ? _.findIndex(allMovieRatings, { label: mvVal })
    : allMovieRatings.length - 1;

  const currentTvRating = allTvRatings[defaultTvRatingIndex];
  const currentMovieRating = allMovieRatings[defaultMovieRatingIndex];
  const tvSliderValue = newTvIdx !== null ? newTvIdx : defaultTvRatingIndex;
  const movieSliderValue = newMovieIdx !== null ? newMovieIdx : defaultMovieRatingIndex;
  const passwordVisibility = parentalControlsShowPassword ? 'text' : 'password';

  let pcsPasswordInput = React.createRef();

  const handleOnChange = e => {
    onFieldChange(e);
    if (pcsPasswordInput.current) {
      pcsPasswordInput.current.focus();
    }
  };

  let passwordErrorText;

  // local error checking
  if (parentalControlsPasswordError) {
    passwordErrorText = passwordInvalidError;
  }

  return (
    <main className={Style.parentalControls}>
      <Header {...headerProps} />
      {!parentalControlsPasswordVerified ? (
        <div className={Style.parentalControlsContainer}>
          <div className={Style.parentalControlsCurrentValues}>
            {currentTvLabel} {currentTvRating.label}
            <span className={Style.parentalControlsCurrentValueMinor}>
              ({currentTvRating.description})
            </span>
            <span>
              {currentMovieLabel} {currentMovieRating.label}
            </span>
            <span className={Style.parentalControlsCurrentValueMinor}>
              ({currentMovieRating.description})
            </span>
          </div>
          <div className={Style.parentalControlsInstructions}>{passwordPrompt}</div>
          <div className={Style.signupPasswordContainer}>
            <input
              className={Style.inputField}
              type={passwordVisibility}
              name="parentalControlsPassword"
              placeholder="Password"
              value={parentalControlsPassword}
              onChange={onFieldChange}
              ref={input => {
                pcsPasswordInput = input;
              }}
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <label className={Style.passwordLabel}>
              <input
                name="parentalControlsShowPassword"
                type="checkbox"
                value={parentalControlsShowPassword.toString()}
                onChange={handleOnChange}
              />
              <span />
            </label>
          </div>
          <div className={Style.error}>{passwordErrorText}</div>
          <button className={Style.btnPrimary} onClick={onVerifyPassword} type="submit">
            {continueLabel}
          </button>
          <ForgotPasswordCTA {...forgotPasswordCTAProps} />
        </div>
      ) : (
        <div className={Style.parentalControlsContainer}>
          <div className={Style.parentalControlsInstructions}>{instructions}</div>
          <div className={Style.parentalControlsCurrentValues}>
            {currentTvLabel} {currentTvRating.label}
            <span className={Style.parentalControlsCurrentValueMinor}>
              ({currentTvRating.description})
            </span>
            <ReactSlider
              className={Style.parentalControlsSlider}
              handleClassName={Style.parentalControlsSliderHandle}
              barClassName={Style.parentalControlsSliderBar}
              withBars
              max={allTvRatings.length - 1}
              value={tvSliderValue}
              onChange={handleTvRatingChange}
            />
            <div className={Style.parentalControlsSliderLabels}>
              {_.map(allTvRatings, (r, idx) => (
                <div key={r.id}>
                  <img
                    className={Style.parentalControlsRatingImage}
                    src={getRatingImageURL(r.label)}
                    alt={r.label}
                  />
                  <ParentalIcons enabled={tvSliderValue >= idx} />
                </div>
              ))}
            </div>
            <span>
              {currentMovieLabel} {currentMovieRating.label}
            </span>
            <span className={Style.parentalControlsCurrentValueMinor}>
              ({currentMovieRating.description})
            </span>
            <ReactSlider
              className={Style.parentalControlsSlider}
              handleClassName={Style.parentalControlsSliderHandle}
              barClassName={Style.parentalControlsSliderBar}
              withBars
              max={allMovieRatings.length - 1}
              value={movieSliderValue}
              onChange={handleMovieRatingChange}
            />
            <div className={Style.parentalControlsSliderLabels}>
              {_.map(allMovieRatings, (r, idx) => (
                <div key={r.id}>
                  <img
                    className={classNames(Style.parentalControlsRatingImage, Style.pcsRatingMovie)}
                    src={getRatingImageURL(r.label)}
                    alt={r.label}
                  />
                  <ParentalIcons enabled={movieSliderValue >= idx} />
                </div>
              ))}
            </div>
          </div>
          <button
            className={Style.btnPrimary}
            onClick={() => onSaveParentalControls(pcs)}
            type="submit"
          >
            {saveLabelText}
          </button>
        </div>
      )}
    </main>
  );
};

const mapStateToProps = state => ({
  pcs: makeSelectAppPCS(state),
});

const mapDispatchToProps = dispatch => ({
  dispatch,
});

ParentalControls.propTypes = {
  account: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  forgotPasswordCTAProps: PropTypes.shape({}).isRequired,
  getRatingImageURL: PropTypes.func.isRequired,
  handleMovieRatingChange: PropTypes.func.isRequired,
  handleTvRatingChange: PropTypes.func.isRequired,
  headerProps: PropTypes.shape({}).isRequired,
  language: PropTypes.shape({
    profile_pcsContinueLabel: PropTypes.string,
    profile_pcsCurrentMovieLabel: PropTypes.string,
    profile_pcsCurrentTvLabel: PropTypes.string,
    profile_pcsInstructions: PropTypes.string,
    profile_pcsPasswordPrompt: PropTypes.string,
    profile_pcsSaveLabel: PropTypes.string,
    profile_passwordInvalidError: PropTypes.string,
  }).isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onParentalControlsBackClick: PropTypes.func.isRequired,
  onSaveParentalControls: PropTypes.func.isRequired,
  onVerifyPassword: PropTypes.func.isRequired,
  parentalControlsNewMovieRatingIndex: PropTypes.number,
  parentalControlsNewTvRatingIndex: PropTypes.number,
  parentalControlsPassword: PropTypes.string.isRequired,
  parentalControlsPasswordError: PropTypes.bool,
  parentalControlsPasswordVerified: PropTypes.bool.isRequired,
  parentalControlsShowPassword: PropTypes.bool.isRequired,
  pcs: PropTypes.shape({}).isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ParentalControls);
